// 多账号数据概览 — 参考微信后台"昨日核心数据"布局
import { Card, Spin, Tag } from 'antd'
import { ClockCircleOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import { useAllMiniprogramOverviews, type MiniprogramData } from '@/mocks/wechatApi'

// 环比箭头组件
function ChangeTag({ value }: { value: number | undefined }) {
  if (value === undefined || value === 0) return <span style={{ color: '#999', fontSize: 11 }}>0%</span>
  const positive = value > 0
  return (
    <span style={{ color: positive ? '#52c41a' : '#ff4d4f', fontSize: 11, fontWeight: 500 }}>
      {positive ? '↑' : '↓'}{Math.abs(value).toFixed(2)}%
    </span>
  )
}

// 单个指标卡片（参考微信后台设计）
function MetricCard({ title, value, unit, changes }: {
  title: string
  value: string
  unit?: string
  changes?: { daily?: number; weekly?: number; monthly?: number }
}) {
  return (
    <div style={{
      background: '#fafafa', borderRadius: 8, padding: '16px 14px 12px',
      border: '1px solid #f0f0f0', minWidth: 0,
    }}>
      <div style={{ color: '#888', fontSize: 12, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', marginBottom: 8, lineHeight: 1.2 }}>
        {value}{unit && <span style={{ fontSize: 13, fontWeight: 400, color: '#666', marginLeft: 2 }}>{unit}</span>}
      </div>
      {changes && (
        <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
          <span>日 <ChangeTag value={changes.daily} /></span>
          <span>周 <ChangeTag value={changes.weekly} /></span>
          <span>月 <ChangeTag value={changes.monthly} /></span>
        </div>
      )}
    </div>
  )
}

// 格式化数字
function fmt(n: number | undefined | null, decimals?: number): string {
  if (n === undefined || n === null) return '0'
  if (decimals !== undefined) return n.toFixed(decimals)
  if (n >= 10000) return (n / 10000).toFixed(2) + '万'
  return n.toLocaleString('zh-CN')
}

function MiniProgramSection({ data }: { data: MiniprogramData }) {
  const c = data.changes || {}
  const raw = data.daily_trend?.date || ''
  const dateStr = raw ? `${raw.slice(0,4)}年${raw.slice(4,6)}月${raw.slice(6,8)}日` : ''

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1a1a1a' }}>
        昨日核心数据
      </div>
      <div style={{ fontSize: 12, color: '#999', marginBottom: 16 }}>{dateStr}</div>

      {/* 第一行：4 个指标 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <MetricCard
          title="累计用户数"
          value={fmt(data.summary.visit_total)}
          changes={{
            daily: c.visit_uv_new_daily,
            weekly: c.visit_uv_new_weekly,
            monthly: c.visit_uv_new_monthly,
          }}
        />
        <MetricCard
          title="打开次数"
          value={fmt(data.daily_trend.session_cnt)}
          changes={{
            daily: c.session_cnt_daily,
            weekly: c.session_cnt_weekly,
            monthly: c.session_cnt_monthly,
          }}
        />
        <MetricCard
          title="访问人数"
          value={fmt(data.daily_trend.visit_uv)}
          changes={{
            daily: c.visit_uv_daily,
            weekly: c.visit_uv_weekly,
            monthly: c.visit_uv_monthly,
          }}
        />
        <MetricCard
          title="新增用户"
          value={fmt(data.daily_trend.visit_uv_new)}
          changes={{
            daily: c.visit_uv_new_daily,
            weekly: c.visit_uv_new_weekly,
            monthly: c.visit_uv_new_monthly,
          }}
        />
      </div>

      {/* 第二行：4 个指标 */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MetricCard
          title="访问页面数"
          value={fmt(data.daily_trend.visit_pv)}
          changes={{
            daily: c.visit_pv_daily,
            weekly: c.visit_pv_weekly,
            monthly: c.visit_pv_monthly,
          }}
        />
        <MetricCard
          title="人均停留时长"
          value={fmt(data.daily_trend.stay_time_uv, 2)}
          unit="秒"
          changes={{
            daily: c.stay_time_uv_daily,
            weekly: c.stay_time_uv_weekly,
            monthly: c.stay_time_uv_monthly,
          }}
        />
        <MetricCard
          title="新增日留存"
          value={data.retain?.daily ? `${data.retain.daily}%` : '-'}
        />
        <MetricCard
          title="总添加人数"
          value="—"
        />
      </div>
    </div>
  )
}

function MPSection({ data }: { data: MiniprogramData }) {
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1a1a1a' }}>
        公众号数据
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 10 }}>
        <MetricCard title="累计粉丝" value={fmt(data.mp_cumulate.cumulate_user || 0)} />
        <MetricCard title="昨日新增" value={fmt(data.mp_summary.new_user || 0)} />
        <MetricCard title="昨日取关" value={fmt(data.mp_summary.cancel_user || 0)} />
        <MetricCard title="文章总数" value={fmt(data.mp_articles?.length || 0)} />
      </div>

      {/* 文章列表 */}
      {data.mp_articles && data.mp_articles.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontWeight: 500, marginBottom: 8, color: '#666', fontSize: 13 }}>最近文章</div>
          {data.mp_articles.slice(0, 5).map((a, i) => (
            <div key={i} style={{ padding: '6px 0', borderBottom: i < Math.min(data.mp_articles.length, 5) - 1 ? '1px solid #f0f0f0' : 'none', fontSize: 13 }}>
              <span style={{ color: '#999', marginRight: 6 }}>📄</span>
              {a.title}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function AccountCard({ data }: { data: MiniprogramData }) {
  const isMiniProgram = data.summary.visit_total > 0
  const isMP = Object.keys(data.mp_summary).length > 0 || Object.keys(data.mp_cumulate).length > 0

  return (
    <Card
      title={
        <span>
          {data.app_name}
          <Tag color={isMiniProgram ? 'blue' : 'green'} style={{ marginLeft: 8 }}>
            {isMiniProgram ? '小程序' : isMP ? '公众号' : '未知'}
          </Tag>
        </span>
      }
      style={{ marginBottom: 24 }}
    >
      {isMiniProgram && <MiniProgramSection data={data} />}
      {isMP && <MPSection data={data} />}

      {!isMiniProgram && !isMP && (
        <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
          暂无数据，请等待凌晨抓取
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: 'right', fontSize: 12, color: '#bbb' }}>
        数据日期: {data.data_date || '-'}
        <ClockCircleOutlined style={{ marginLeft: 4 }} />
      </div>
    </Card>
  )
}

export default function AllWechatApis() {
  const { data, isLoading, error } = useAllMiniprogramOverviews()

  if (error) {
    return (
      <div>
        <PageHeader title="多账号数据概览" subtitle="API 数据加载失败" />
        <Card><div style={{ color: '#ff4d4f', textAlign: 'center', padding: '40px 0' }}>加载失败: {String(error)}</div></Card>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="多账号数据概览"
        subtitle="小程序 + 公众号核心指标一览"
      />

      <Spin spinning={isLoading}>
        {data && data.length > 0 ? (
          data.map((item) => <AccountCard key={item.app_id} data={item} />)
        ) : (
          <Card style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            暂无公众号/小程序配置
          </Card>
        )}
      </Spin>
    </div>
  )
}
